import {FragmentDefinitionNode} from 'graphql';

import {assert, assertAndContinue} from 'Asserts';

import BaseFieldTranslator from './BaseFieldTranslator';
import CustomFieldTranslator from './CustomFieldTranslator';
import {DeclarativeTranslationsMapping} from './DeclarativeTranslationsMapping';
import IdentityFieldTranslator from './IdentityFieldTranslator';
import NestedFieldTranslator from './NestedFieldTranslator';
import {PossibleTypesMapping} from './PossibleTypesMapping';
import {
  GraphQLSelectionSet,
  HasAsymmetricFieldResolver,
  RESTField,
  RESTSelectionSet,
} from './preprocessedData';
import SimpleNameFieldTranslator from './SimpleNameFieldTranslator';
import {BaseTypenameResolver, getTypenameResolver} from './TypenameResolver';
import {RESTSelectionSetOptions} from './Types';

const TRANSLATOR_CLASSES: Array<typeof BaseFieldTranslator> = [
  // @ts-expect-error
  CustomFieldTranslator,
  // @ts-expect-error
  NestedFieldTranslator,
  // @ts-expect-error
  IdentityFieldTranslator,
  SimpleNameFieldTranslator,
];

function validateParsedField(parsedRESTField: RESTField, graphQLSelectionSet: GraphQLSelectionSet): void {
  if (parsedRESTField.customGraphQLName) {
    assert(
      graphQLSelectionSet.fieldsByName[parsedRESTField.customGraphQLName] != null,
      `Found custom GraphQL field name in REST field=${parsedRESTField.fieldName} without corresponding ` +
        `field in selection set. Expected to find ${parsedRESTField.customGraphQLName}`
    );
  }
}

function parseFieldTranslator(
  declarationName: string,
  parsedRESTField: RESTField,
  graphQLSelectionSet: GraphQLSelectionSet,
  possibleTypesMapping: PossibleTypesMapping
): BaseFieldTranslator {
  validateParsedField(parsedRESTField, graphQLSelectionSet);

  for (const translatorCls of TRANSLATOR_CLASSES) {
    // @ts-expect-error See comment in BaseFieldTranslator
    const translator: BaseFieldTranslator | null = translatorCls.tryParse(
      declarationName,
      parsedRESTField,
      graphQLSelectionSet,
      possibleTypesMapping
    );
    if (translator) {
      return translator;
    }
  }

  throw new Error(`Expected to find a matching translator for REST field=${parsedRESTField.fieldName}`);
}

function assertNoDuplicates(fieldNames: Array<string>, message: string): void {
  assert(fieldNames.length === new Set(fieldNames).size, message);
}

function hasSameItems<T>(lhs: Array<T>, rhs: Array<T>): boolean {
  return lhs.length === rhs.length && lhs.every((key) => rhs.includes(key));
}

function objSize(obj: {}): number {
  return Object.keys(obj).length;
}

function parseFieldTranslators(
  declarationName: string,
  restSelectionSet: RESTSelectionSet,
  graphQLSelectionSet: GraphQLSelectionSet,
  possibleTypesMapping: PossibleTypesMapping
): Array<BaseFieldTranslator> {
  assert(
    objSize(restSelectionSet.fieldsByName) ===
      objSize(graphQLSelectionSet.fieldsByName) - objSize(restSelectionSet.graphQLOnlyFieldsByName),
    `GraphQL selection set and REST declaration must have the same number of fields\n` +
      `    REST fields: ${Object.keys(restSelectionSet.fieldsByName)}\n` +
      `    GraphQL fields: ${Object.keys(graphQLSelectionSet.fieldsByName)}\n`
  );

  const fieldTranslators = Object.values(restSelectionSet.fieldsByName).map((parsedRESTField) =>
    parseFieldTranslator(declarationName, parsedRESTField, graphQLSelectionSet, possibleTypesMapping)
  );

  assertNoDuplicates(
    fieldTranslators.map((translator) => translator.restKey),
    'Multiple translators attempting to handle the same REST field'
  );
  assertNoDuplicates(
    fieldTranslators.map((translator) => translator.graphQLKey),
    'Multiple translators attempting to handle the same GraphQL field'
  );

  return fieldTranslators;
}

interface UnionFieldTranslatorsByTypename {
  [unionTypename: string]: SelectionSetTranslator;
}

function parseUnionFieldTranslatorsByTypename(
  restSelectionSet: RESTSelectionSet,
  graphQLSelectionSet: GraphQLSelectionSet,
  possibleTypesMapping: PossibleTypesMapping
): UnionFieldTranslatorsByTypename {
  const restDeclarationUnions = restSelectionSet.declarationUnions;
  const graphQLInlineFragments = graphQLSelectionSet.inlineFragments;

  assert(
    hasSameItems(Object.keys(restDeclarationUnions), Object.keys(graphQLSelectionSet.inlineFragments)),
    `Expected to find corresponding union types and inline fragments:\n\n` +
      `REST=${Object.keys(restDeclarationUnions)}\n\n` +
      `GraphQL=${Object.keys(graphQLInlineFragments)}`
  );

  // eslint-disable-next-line benchlint/no-spread-reduce
  return Object.keys(restDeclarationUnions).reduce(
    (memo, typename) => ({
      ...memo,
      [typename]: SelectionSetTranslator.build(
        typename,
        restDeclarationUnions[typename],
        graphQLInlineFragments[typename],
        possibleTypesMapping
      ),
    }),
    {} as UnionFieldTranslatorsByTypename
  );
}

function resolveAsymmetricFields(
  asymmetricResolversByFieldName: {[fieldName: string]: HasAsymmetricFieldResolver},
  sourceObj: TranslatableValue
): TranslatableValue {
  const asymmetricFields: TranslatableValue = {};
  for (const [fieldName, field] of Object.entries(asymmetricResolversByFieldName)) {
    const translatedFieldValue = field.resolveValue(sourceObj);
    const shouldIncludeField =
      field.getAsymmetricFieldResolver().includeIfNull || translatedFieldValue != null;
    const excludeCondition = field.getAsymmetricFieldResolver().excludeCondition;
    const shouldExcludeField = excludeCondition != null && excludeCondition(sourceObj);
    if (shouldIncludeField && !shouldExcludeField) {
      asymmetricFields[fieldName] = translatedFieldValue;
    }
  }
  return asymmetricFields;
}

export interface TranslatableValue {
  [key: string]: unknown;
}

class SelectionSetTranslator {
  declarationName: string;
  possibleTypesMapping: PossibleTypesMapping;
  fieldTranslators: Array<BaseFieldTranslator>;
  spreadTranslators: Array<SelectionSetTranslator>;
  unionFieldTranslatorsByTypename: UnionFieldTranslatorsByTypename;
  restSelectionSet: RESTSelectionSet;
  graphQLSelectionSet: GraphQLSelectionSet;

  constructor(
    declarationName: string,
    possibleTypesMapping: PossibleTypesMapping,
    fieldTranslators: Array<BaseFieldTranslator>,
    spreadTranslators: Array<SelectionSetTranslator>,
    unionFieldTranslatorsByTypename: UnionFieldTranslatorsByTypename,
    restSelectionSet: RESTSelectionSet,
    graphQLSelectionSet: GraphQLSelectionSet
  ) {
    this.declarationName = declarationName;
    this.possibleTypesMapping = possibleTypesMapping;
    this.fieldTranslators = fieldTranslators;
    this.spreadTranslators = spreadTranslators;
    this.unionFieldTranslatorsByTypename = unionFieldTranslatorsByTypename;
    this.restSelectionSet = restSelectionSet;
    this.graphQLSelectionSet = graphQLSelectionSet;
  }

  public static build(
    declarationName: string,
    restSelectionSet: RESTSelectionSet,
    graphQLSelectionSet: GraphQLSelectionSet,
    possibleTypesMapping: PossibleTypesMapping
  ): SelectionSetTranslator {
    const spreadTranslators = Object.keys(restSelectionSet.declarationSpreads).map((spreadName) =>
      SelectionSetTranslator.build(
        declarationName,
        restSelectionSet.declarationSpreads[spreadName],
        graphQLSelectionSet.getSpreadByDeclarationName(spreadName),
        possibleTypesMapping
      )
    );

    return new SelectionSetTranslator(
      declarationName,
      possibleTypesMapping,
      parseFieldTranslators(declarationName, restSelectionSet, graphQLSelectionSet, possibleTypesMapping),
      spreadTranslators,
      parseUnionFieldTranslatorsByTypename(restSelectionSet, graphQLSelectionSet, possibleTypesMapping),
      restSelectionSet,
      graphQLSelectionSet
    );
  }

  private getUnionFieldTranslatorsForTypename(
    unionFieldTranslatorsByTypename: UnionFieldTranslatorsByTypename,
    typename: string
  ): Array<SelectionSetTranslator> {
    return Object.entries(unionFieldTranslatorsByTypename)
      .filter(([unionTypename]) =>
        this.possibleTypesMapping.concreteTypeSatisfiesType(typename, unionTypename)
      )
      .map(([_, translator]) => translator);
  }

  private getGQLValue(fieldTranslator: BaseFieldTranslator, restObj: TranslatableValue): unknown {
    const restObjHasKey = restObj.hasOwnProperty(fieldTranslator.restKey);

    assertAndContinue(
      restObjHasKey || fieldTranslator.excludeCondition != null,
      `Expected to find REST field=${fieldTranslator.restKey} in obj when translating ${
        this.declarationName
      }.\nAvailable fields: ${Object.keys(restObj)}`
    );

    if (!restObjHasKey) {
      return fieldTranslator.excludeCondition?.gqlDefaultValue;
    }

    return fieldTranslator.getGQLValue(restObj);
  }

  public restToGQL(typename: string, restObj: TranslatableValue): TranslatableValue {
    const unionTranslators = this.getUnionFieldTranslatorsForTypename(
      this.unionFieldTranslatorsByTypename,
      typename
    );

    let gqlObj: TranslatableValue = resolveAsymmetricFields(
      this.restSelectionSet.graphQLOnlyFieldsByName,
      restObj
    );
    for (const nestedTranslator of [...unionTranslators, ...this.spreadTranslators]) {
      gqlObj = {...gqlObj, ...nestedTranslator.restToGQL(typename, restObj)};
    }
    for (const fieldTranslator of this.fieldTranslators) {
      gqlObj[fieldTranslator.graphQLKey] = this.getGQLValue(fieldTranslator, restObj);
    }

    gqlObj.__typename = typename;
    return gqlObj;
  }

  public gqlToREST(gqlObj: TranslatableValue): TranslatableValue {
    // @ts-expect-error Converting unknown to string
    const typename: string = gqlObj.__typename;

    const unionTranslators = this.getUnionFieldTranslatorsForTypename(
      this.unionFieldTranslatorsByTypename,
      typename
    );

    let restObj: TranslatableValue = resolveAsymmetricFields(
      this.restSelectionSet.restOnlyFieldsByName,
      gqlObj
    );
    for (const unionTranslator of [...unionTranslators, ...this.spreadTranslators]) {
      restObj = {...restObj, ...unionTranslator.gqlToREST(gqlObj)};
    }
    for (const fieldTranslator of this.fieldTranslators) {
      const translatedFieldValue = fieldTranslator.getRESTValue(gqlObj);
      const shouldExcludeRESTField =
        fieldTranslator.excludeCondition != null &&
        fieldTranslator.excludeCondition.shouldExcludeFunction(translatedFieldValue, gqlObj);
      if (!shouldExcludeRESTField) {
        restObj[fieldTranslator.restKey] = translatedFieldValue;
      }
    }

    return restObj;
  }
}

export class DeclarativeTranslator {
  declarationName: string;
  typenameResolver: BaseTypenameResolver;
  selectionSetTranslator: SelectionSetTranslator;

  constructor(
    declarationName: string,
    typenameResolver: BaseTypenameResolver,
    selectionSetTranslator: SelectionSetTranslator
  ) {
    this.declarationName = declarationName;
    this.typenameResolver = typenameResolver;
    this.selectionSetTranslator = selectionSetTranslator;
  }

  public static build(
    declarationName: string,
    restSelectionSet: RESTSelectionSet,
    graphQLSelectionSet: GraphQLSelectionSet,
    possibleTypesMapping: PossibleTypesMapping
  ): DeclarativeTranslator {
    return new DeclarativeTranslator(
      declarationName,
      getTypenameResolver(restSelectionSet.typenameResolverName),
      SelectionSetTranslator.build(
        declarationName,
        restSelectionSet,
        graphQLSelectionSet,
        possibleTypesMapping
      )
    );
  }

  public restToGQL(restObj: TranslatableValue): TranslatableValue {
    const typename = this.typenameResolver.getTypenameFromObj(restObj);
    return this.selectionSetTranslator.restToGQL(typename, restObj);
  }

  public gqlToREST(gqlObj: TranslatableValue): TranslatableValue {
    return this.selectionSetTranslator.gqlToREST(gqlObj);
  }
}

export function buildDeclarativeTranslator(
  declarationName: string,
  restJSON: RESTSelectionSetOptions,
  gqlFragmentDefinition: FragmentDefinitionNode,
  declarativeTranslationsMapping: DeclarativeTranslationsMapping,
  possibleTypesMapping: PossibleTypesMapping
): DeclarativeTranslator {
  const restSelectionSet = RESTSelectionSet.parse(restJSON, declarativeTranslationsMapping);
  const graphQLSelectionSet = GraphQLSelectionSet.create(
    gqlFragmentDefinition,
    declarativeTranslationsMapping
  );

  return DeclarativeTranslator.build(
    declarationName,
    restSelectionSet,
    graphQLSelectionSet,
    possibleTypesMapping
  );
}
